import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, User, Mail, Phone, Calendar, Shield, Lock, Eye, EyeOff, KeyRound, Camera, Trash2, Building2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import ProfileImageCropModal from '../components/ProfileImageCropModal';
import AdminSidebar from '../components/AdminSidebar';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import { getAllProvinces, getDistricts } from '../data/regions';

interface ProfileFormData {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  province: string;
  district: string;
  birthDate: string;
  gender: 'male' | 'female' | '';
  memberType: string;
  companyName: string;
  businessCategory: 'FITNESS' | 'PILATES';
}

interface PasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

// 비밀번호 유효성 검사
const validatePassword = (password: string): string | null => {
  if (!password) return '비밀번호를 입력해주세요.';
  if (password.length < 8) return '8자 이상 입력해주세요.';
  if (!/[a-zA-Z]/.test(password)) return '영문을 포함해주세요.';
  if (!/[0-9]/.test(password)) return '숫자를 포함해주세요.';
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return '특수문자를 포함해주세요.';
  return null;
};

export default function ProfilePage() {
  const { user, isAuthenticated, checkSession } = useAuthStore();
  const navigate = useNavigate();

  // 비밀번호 확인 상태
  const [isVerified, setIsVerified] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);

  // 프로필 상태
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    province: '',
    district: '',
    birthDate: '',
    gender: '',
    memberType: 'user',
    companyName: '',
    businessCategory: 'FITNESS',
  });

  // 비밀번호 변경 상태
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    newPassword: '',
    confirmPassword: '',
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 프로필 이미지 상태
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 사업자등록증/민간자격증 상태 (provider 전용)
  const [certificateUrl, setCertificateUrl] = useState<string | null>(null);
  const [businessApproved, setBusinessApproved] = useState(false);
  const [isUploadingCertificate, setIsUploadingCertificate] = useState(false);
  const certificateInputRef = useRef<HTMLInputElement>(null);

  // 비로그인 시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 소셜 로그인 사용자는 비밀번호 확인 없이 바로 접근
  useEffect(() => {
    if (user && user.provider !== 'email') {
      setIsVerified(true);
    }
  }, [user]);

  // 비밀번호 확인 API
  const handleVerifyPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyError(null);
    setIsVerifying(true);

    try {
      const response = await fetch('/api/v1/auth/verify-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: verifyPassword }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsVerified(true);
      } else {
        setVerifyError(data.error || '비밀번호가 올바르지 않습니다.');
      }
    } catch (error) {
      setVerifyError('서버 오류가 발생했습니다.');
    } finally {
      setIsVerifying(false);
    }
  };

  // 영문 이름 분리 함수 - 기존 데이터는 순서를 알 수 없으므로 전체를 firstName에 표시
  const splitEnglishName = (fullName: string): { firstName: string; lastName: string } => {
    if (!fullName) return { firstName: '', lastName: '' };

    // 기존 데이터는 어떤 순서로 입력됐는지 알 수 없으므로
    // 전체 이름을 firstName에 표시하고 사용자가 직접 수정하도록 함
    return { firstName: fullName.trim(), lastName: '' };
  };

  // 이름 합치기 및 정리
  const cleanAndConcatenateName = (firstName: string, lastName: string): string => {
    const cleanFirst = firstName.trim().replace(/\s+/g, ' ');
    const cleanLast = lastName.trim().replace(/\s+/g, ' ');
    if (!cleanLast) return cleanFirst;
    return `${cleanFirst} ${cleanLast}`.trim();
  };

  // 사용자 정보 불러오기
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user || !isVerified) return;

      setIsLoading(true);
      try {
        const response = await fetch('/api/v1/auth/profile', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();

          // 새 필드가 있으면 사용, 없으면 기존 name_en을 split
          let firstName = data.first_name_en || '';
          let lastName = data.last_name_en || '';

          if (!firstName && !lastName && data.name_en) {
            const split = splitEnglishName(data.name_en);
            firstName = split.firstName;
            lastName = split.lastName;
          }

          setFormData({
            name: data.name || user.name || '',
            firstName,
            lastName,
            email: data.email || user.email || '',
            phone: data.phone || '',
            province: data.province || '',
            district: data.district || '',
            birthDate: data.birth_date || '',
            gender: data.gender || '',
            memberType: data.member_type || 'user',
            companyName: data.company_name || '',
            businessCategory: data.business_category || 'FITNESS',
          });
          setAvatarUrl(data.avatar_url || null);
          setCertificateUrl(data.business_certificate_url || null);
          setBusinessApproved(data.business_approved === true);
        } else {
          setFormData({
            name: user.name || '',
            firstName: '',
            lastName: '',
            email: user.email || '',
            phone: '',
            province: '',
            district: '',
            birthDate: '',
            gender: '',
            memberType: 'user',
            companyName: '',
            businessCategory: 'FITNESS',
          });
        }
      } catch (error) {
        setFormData({
          name: user.name || '',
          firstName: '',
          lastName: '',
          email: user.email || '',
          phone: '',
          province: '',
          district: '',
          birthDate: '',
          gender: '',
          memberType: 'user',
          companyName: '',
          businessCategory: 'FITNESS',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [user, isVerified]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setMessage(null);
  };

  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const province = e.target.value;
    setFormData((prev) => ({ ...prev, province, district: '' }));
    setMessage(null);
  };

  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const district = e.target.value;
    setFormData((prev) => ({ ...prev, district }));
    setMessage(null);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setPasswordMessage(null);

    // 실시간 유효성 검사
    if (name === 'newPassword') {
      const error = validatePassword(value);
      setPasswordErrors((prev) => ({ ...prev, newPassword: error || undefined }));
    }
    if (name === 'confirmPassword') {
      if (value !== passwordData.newPassword && name === 'confirmPassword') {
        setPasswordErrors((prev) => ({ ...prev, confirmPassword: '비밀번호가 일치하지 않습니다.' }));
      } else {
        setPasswordErrors((prev) => ({ ...prev, confirmPassword: undefined }));
      }
    }
  };

  // 비밀번호 확인 필드 blur 시 일치 여부 검사
  const handleConfirmPasswordBlur = () => {
    if (passwordData.confirmPassword && passwordData.confirmPassword !== passwordData.newPassword) {
      setPasswordErrors((prev) => ({ ...prev, confirmPassword: '비밀번호가 일치하지 않습니다.' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await fetch('/api/v1/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          name_en: cleanAndConcatenateName(formData.firstName, formData.lastName),
          first_name_en: formData.firstName,
          last_name_en: formData.lastName,
          phone: formData.phone,
          province: formData.province,
          district: formData.district,
          birth_date: formData.birthDate,
          gender: formData.gender,
          ...((formData.memberType === 'provider' || formData.memberType === 'employer') && {
            business_category: formData.businessCategory,
          }),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: '프로필이 성공적으로 업데이트되었습니다.' });
        await checkSession();
      } else {
        setMessage({ type: 'error', text: data.error || '프로필 업데이트에 실패했습니다.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '서버 오류가 발생했습니다. 다시 시도해주세요.' });
    } finally {
      setIsSaving(false);
    }
  };

  // 사업자등록증/민간자격증 교체 핸들러 (provider/admin)
  const handleCertificateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // 같은 파일 재선택 허용

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'PDF, JPG, PNG 파일만 업로드 가능합니다.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: '파일 크기는 10MB 이하여야 합니다.' });
      return;
    }

    setIsUploadingCertificate(true);
    try {
      const fd = new FormData();
      fd.set('certificate', file);
      const res = await fetch('/api/v1/profile/certificate', {
        method: 'PUT',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '업로드에 실패했습니다.');
      setCertificateUrl(data.url);
      setMessage({ type: 'success', text: '인증서가 교체되었습니다.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : '업로드 중 오류가 발생했습니다.',
      });
    } finally {
      setIsUploadingCertificate(false);
    }
  };

  // 이미지 선택 핸들러
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 타입 검증
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: '이미지 파일만 업로드할 수 있습니다.' });
      return;
    }

    // 파일 크기 검증 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '이미지 크기는 5MB 이하여야 합니다.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);

    // input 초기화 (같은 파일 재선택 가능)
    e.target.value = '';
  };

  // 크롭된 이미지 저장
  const handleCropSave = async (croppedImage: Blob) => {
    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('image', croppedImage, 'profile.png');

      const response = await fetch('/api/v1/auth/profile/image', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAvatarUrl(data.imageUrl);
        setShowCropModal(false);
        setSelectedImage(null);
        setMessage({ type: 'success', text: '프로필 사진이 업데이트되었습니다.' });
        await checkSession(); // 세션 갱신
      } else {
        setMessage({ type: 'error', text: data.error || '프로필 사진 업로드에 실패했습니다.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '서버 오류가 발생했습니다.' });
    } finally {
      setIsUploadingImage(false);
    }
  };

  // 프로필 이미지 삭제
  const handleDeleteImage = async () => {
    if (!avatarUrl) return;

    if (!confirm('프로필 사진을 삭제하시겠습니까?')) return;

    setIsUploadingImage(true);

    try {
      const response = await fetch('/api/v1/auth/profile/image', {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAvatarUrl(null);
        setMessage({ type: 'success', text: '프로필 사진이 삭제되었습니다.' });
        await checkSession(); // 세션 갱신
      } else {
        setMessage({ type: 'error', text: data.error || '프로필 사진 삭제에 실패했습니다.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '서버 오류가 발생했습니다.' });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검사
    const newPasswordError = validatePassword(passwordData.newPassword);
    if (newPasswordError) {
      setPasswordErrors({ newPassword: newPasswordError });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordErrors({ confirmPassword: '비밀번호가 일치하지 않습니다.' });
      return;
    }

    setIsChangingPassword(true);
    setPasswordMessage(null);

    try {
      const response = await fetch('/api/v1/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: verifyPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordMessage({ type: 'success', text: '비밀번호가 성공적으로 변경되었습니다.' });
        setPasswordData({ newPassword: '', confirmPassword: '' });
      } else {
        setPasswordMessage({ type: 'error', text: data.error || '비밀번호 변경에 실패했습니다.' });
      }
    } catch (error) {
      setPasswordMessage({ type: 'error', text: '서버 오류가 발생했습니다.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  // 비밀번호 확인 화면 (이메일 로그인 사용자만)
  if (!isVerified && user.provider === 'email') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
          {/* 사이드바 표시 */}
          <AdminSidebar />

          <main className="flex-1">
            <div className="max-w-md mx-auto px-4 py-12">
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-primary" />
                  </div>
                  <h1 className="text-xl font-bold text-gray-900">비밀번호 확인</h1>
                  <p className="text-gray-500 mt-2">
                    내 정보를 확인하려면 비밀번호를 입력해주세요.
                  </p>
                </div>

                <form onSubmit={handleVerifyPassword} className="space-y-4">
                  <div>
                    <label htmlFor="verifyPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      현재 비밀번호
                    </label>
                    <div className="relative">
                      <input
                        type={showVerifyPassword ? 'text' : 'password'}
                        id="verifyPassword"
                        value={verifyPassword}
                        onChange={(e) => {
                          setVerifyPassword(e.target.value);
                          setVerifyError(null);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all pr-12"
                        placeholder="비밀번호를 입력하세요"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowVerifyPassword(!showVerifyPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showVerifyPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {verifyError && (
                    <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">
                      {verifyError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isVerifying || !verifyPassword}
                    className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isVerifying ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        확인 중...
                      </>
                    ) : (
                      '확인'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        {/* 사이드바 표시 */}
        <AdminSidebar />

        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div className="max-w-2xl mx-auto">
            {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 프로필 정보 폼 */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 프로필 이미지 */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                {/* 프로필 사진 */}
                <div className="relative group">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="프로필 사진"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold">
                      {formData.name?.charAt(0) || formData.email?.charAt(0) || 'U'}
                    </div>
                  )}

                  {/* 호버 오버레이 */}
                  <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-1.5 bg-white/90 rounded-full hover:bg-white transition-colors"
                      title="사진 변경"
                    >
                      <Camera className="w-4 h-4 text-gray-700" />
                    </button>
                    {avatarUrl && (
                      <button
                        type="button"
                        onClick={handleDeleteImage}
                        className="p-1.5 bg-white/90 rounded-full hover:bg-white transition-colors"
                        title="사진 삭제"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>

                  {/* 업로드 중 로딩 */}
                  {isUploadingImage && (
                    <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    </div>
                  )}
                </div>

                {/* 숨겨진 파일 입력 */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />

                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{formData.name || '사용자'}</h2>
                  <p className="text-sm text-gray-500">{formData.email}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Shield className="w-3 h-3 text-green-500" />
                    <span className="text-xs text-green-600">
                      {user.provider === 'email' ? '이메일 인증됨' : `${user.provider} 연동`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 text-sm text-primary hover:text-primary-600 font-medium"
                  >
                    프로필 사진 {avatarUrl ? '변경' : '등록'}
                  </button>
                </div>
              </div>
            </div>

            {/* 사업자 회원 정보 (교육 기관/기업 회원) */}
            {(formData.memberType === 'provider' || formData.memberType === 'employer') && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {formData.memberType === 'provider' ? '교육 기관 정보' : '기업 정보'}
                </h3>
                <div className="space-y-4">
                  {/* 기업명 (읽기 전용) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Building2 className="w-4 h-4 inline mr-1" />
                      기업명
                    </label>
                    <input
                      type="text"
                      value={formData.companyName}
                      disabled
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">기업명 변경은 관리자에게 문의하세요.</p>
                  </div>

                  {/* 업종 카테고리 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      업종 카테고리
                    </label>
                    <div className="flex gap-4">
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.businessCategory === 'FITNESS'
                            ? 'border-accent bg-accent/5 text-accent'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="businessCategory"
                          value="FITNESS"
                          checked={formData.businessCategory === 'FITNESS'}
                          onChange={(e) => setFormData(prev => ({ ...prev, businessCategory: e.target.value as 'FITNESS' | 'PILATES' }))}
                          className="sr-only"
                        />
                        <span className="font-medium">피트니스</span>
                      </label>
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.businessCategory === 'PILATES'
                            ? 'border-accent bg-accent/5 text-accent'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="businessCategory"
                          value="PILATES"
                          checked={formData.businessCategory === 'PILATES'}
                          onChange={(e) => setFormData(prev => ({ ...prev, businessCategory: e.target.value as 'FITNESS' | 'PILATES' }))}
                          className="sr-only"
                        />
                        <span className="font-medium">필라테스</span>
                      </label>
                    </div>
                  </div>

                  {/* 사업자등록증/민간자격증 — 교육 기관(provider)만 노출 */}
                  {formData.memberType === 'provider' && (
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          사업자등록증 / 민간자격증
                        </label>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            businessApproved
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {businessApproved ? '승인 완료' : '승인 대기'}
                        </span>
                      </div>
                      {certificateUrl ? (
                        <a
                          href={certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-sm text-accent hover:underline mb-3"
                        >
                          현재 업로드된 파일 보기 ↗
                        </a>
                      ) : (
                        <p className="text-sm text-gray-500 mb-3">아직 업로드된 파일이 없습니다.</p>
                      )}
                      <input
                        ref={certificateInputRef}
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={handleCertificateUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => certificateInputRef.current?.click()}
                        disabled={isUploadingCertificate}
                        className="px-4 py-2 bg-accent/10 text-accent border border-accent/40 rounded-lg text-sm font-medium hover:bg-accent/20 disabled:opacity-60"
                      >
                        {isUploadingCertificate
                          ? '업로드 중...'
                          : certificateUrl
                          ? '파일 교체'
                          : '파일 업로드'}
                      </button>
                      <p className="text-xs text-gray-500 mt-2">
                        PDF·JPG·PNG, 최대 10MB. 파일을 교체해도 기존 승인 상태는 유지됩니다.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 기본 정보 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">기본 정보</h3>

              <div className="space-y-4">
                {/* 이름 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    이름
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="이름을 입력하세요"
                  />
                </div>

                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    First Name (이름)
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="Gildong"
                  />
                  <p className="text-xs text-gray-500 mt-1">기존 이름을 확인하고 First Name과 Last Name으로 나눠서 입력해주세요.</p>
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Last Name (성)
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="Hong"
                  />
                  <p className="text-xs text-gray-500 mt-1">인증서에 "Gildong Hong" 형식으로 표시됩니다.</p>
                </div>

                {/* 이메일 (읽기 전용) */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    이메일
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    disabled
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">이메일은 변경할 수 없습니다.</p>
                </div>

                {/* 전화번호 */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    전화번호
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="010-0000-0000"
                  />
                </div>

                {/* 시/도 */}
                <div>
                  <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
                    시/도
                  </label>
                  <select
                    id="province"
                    value={formData.province}
                    onChange={handleProvinceChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  >
                    <option value="">시/도 선택</option>
                    {getAllProvinces().map((province) => (
                      <option key={province} value={province}>
                        {province}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 시/군/구 */}
                <div>
                  <label htmlFor="district" className="block text-sm font-medium text-gray-700 mb-1">
                    시/군/구
                  </label>
                  <select
                    id="district"
                    value={formData.district}
                    onChange={handleDistrictChange}
                    disabled={!formData.province}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      !formData.province ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  >
                    <option value="">시/군/구 선택</option>
                    {formData.province && getDistricts(formData.province).map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 생년월일 */}
                <div>
                  <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    생년월일
                  </label>
                  <input
                    type="date"
                    id="birthDate"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>

                {/* 성별 */}
                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                    성별
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  >
                    <option value="">선택안함</option>
                    <option value="male">남성</option>
                    <option value="female">여성</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 프로필 저장 메시지 */}
            {message && (
              <div
                className={`p-4 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {message.text}
              </div>
            )}

            {/* 프로필 저장 버튼 */}
            <button
              type="submit"
              disabled={isSaving}
              className="w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-white font-semibold py-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  저장 중...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  변경사항 저장
                </>
              )}
            </button>
          </form>

          {/* 비밀번호 변경 섹션 (이메일 로그인 사용자만) */}
          {user.provider === 'email' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <KeyRound className="w-5 h-5" />
                  비밀번호 변경
                </h3>

                <div className="space-y-4">
                  {/* 새 비밀번호 */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      새 비밀번호
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all pr-12 ${
                          passwordErrors.newPassword ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="영문, 숫자, 특수문자 포함 8자 이상"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <p className="mt-1 text-sm text-red-500">{passwordErrors.newPassword}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      영문, 숫자, 특수문자를 포함하여 8자 이상 입력해주세요.
                    </p>
                  </div>

                  {/* 비밀번호 확인 */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      비밀번호 확인
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        onBlur={handleConfirmPasswordBlur}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all pr-12 ${
                          passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="새 비밀번호 재입력"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-500">{passwordErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 비밀번호 변경 메시지 */}
              {passwordMessage && (
                <div
                  className={`p-4 rounded-lg ${
                    passwordMessage.type === 'success'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {passwordMessage.text}
                </div>
              )}

              {/* 비밀번호 변경 버튼 */}
              <button
                type="submit"
                disabled={isChangingPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-600 text-white font-semibold py-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isChangingPassword ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    변경 중...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-5 h-5" />
                    비밀번호 변경
                  </>
                )}
              </button>
            </form>
          )}
            </div>
          )}
          </div>
        </main>
      </div>

      <Footer />

      {/* 프로필 이미지 크롭 모달 */}
      {showCropModal && selectedImage && (
        <ProfileImageCropModal
          imageUrl={selectedImage}
          onClose={() => {
            setShowCropModal(false);
            setSelectedImage(null);
          }}
          onSave={handleCropSave}
          isSaving={isUploadingImage}
        />
      )}
    </div>
  );
}
